import { Button, ButtonMode, ButtonSize, Icon, ICONS, Widget } from '@creditsights/styleguide-web';
// eslint-disable-next-line import/no-unresolved
import { RP_CALCULATOR_WIDTH, RP_CALCULATOR_HEIGHT } from '@creditsights/rp-calculator-web';
import classNames from 'classnames/bind';
import React, { forwardRef, ReactElement, useMemo } from 'react';
import { Accordion } from 'semantic-ui-react';
import { EXTERNAL_ROUTES } from '../../../Base.constants';
import { AccordionWidgetData } from '../../../models/RiskProducts';
import { MODAL_TYPES, useGlobalModalContext } from '../../GlobalModal';
import { GenericModalProps } from '../../GlobalModal/GenericModal/GenericModal';
import styles from '../AcordionList.module.scss';
import ExpandableMetric from '../common/ExpandableMetric';
import { getFundamentalsData } from './FundamentalsWidget.logic';
import { FundamentalsProps } from './FundamentalsWidget.types';
import { FUNDAMENTAL_DATE } from '../RiskOverview/RiskOverviewWidget.constants';
import { DATE_FORMATS, formatDate } from '../../../utils/dateUtils';

const cx = classNames.bind(styles);
const FundamentalsWidget = (
  {
    fundamentalsMetrics,
    isError,
    onErrorRetry,
    showCalculatorButton = false,
    ...sortableWidgetProps
  }: FundamentalsProps,
  ref
): ReactElement | null => {
  const fundamentalsData = useMemo(() => {
    return getFundamentalsData(fundamentalsMetrics);
  }, [fundamentalsMetrics]);

  const areChildrenEmpty = (metric: AccordionWidgetData): boolean => {
    return metric.children.every((item) => item.value == null);
  };

  const renderFundamentals = () => {
    return fundamentalsData.map((metric) => {
      return (
        <ExpandableMetric
          key={`fundamentals-${metric.item.label}`}
          isItemExpanded={!areChildrenEmpty(metric)}
          isTitleValueShown={false}
          metric={metric}
        />
      );
    });
  };

  const renderNotes = () => {
    return (
      <div className={cx('notes')} data-testid="notes">
        <div className={cx('text')}>All figures in millions of {fundamentalsMetrics?.currency}</div>
        <div className={cx('text')}>
          {FUNDAMENTAL_DATE}
          <span className={cx('info')}>
            {formatDate(fundamentalsMetrics?.fund_date, DATE_FORMATS.MONTH_ABBR_DATE, '-')}
          </span>
        </div>
      </div>
    );
  };

  const onCalculatorButtonClick = () => {
    window.open(
      `${EXTERNAL_ROUTES.rpCalculator}?companyId=${fundamentalsMetrics?.cs_companyid}`,
      'RP Calculator',
      `width=${RP_CALCULATOR_WIDTH},height=${RP_CALCULATOR_HEIGHT}`
    );
  };

  const renderCalculatorButton = () =>
    showCalculatorButton ? (
      <Button
        mode={ButtonMode.PRIMARY}
        size={ButtonSize.SMALL}
        onClick={onCalculatorButtonClick}
        data-testid="risk-calculator-button"
      >
        Calculator
        <span className={cx('beta')}>beta</span>
        <Icon name={ICONS.ExternalWindow} width={12} height={12} />
      </Button>
    ) : (
      <>&nbsp;</>
    );

  const { showModal } = useGlobalModalContext();

  const onClick = () => {
    const modalProps: GenericModalProps = {
      headerTitle: 'Risk Fundamentals',
      content:
        '<p>CreditSights’ Risk Products models are based on data sourced from FactSet (for non-financial companies) and SNL (for financials). The Risk Fundamentals data displayed here represent calculations based on those sources. All income statement and cash flow statement items are LTM.</p>',
      dataTestId: 'fundamentals-modal',
    };
    showModal(MODAL_TYPES.GENERIC, modalProps);
  };

  return (
    <Widget
      headerTitle="Risk Fundamentals"
      dataTestId="fundamentals-widget"
      headerTooltip={{ tooltipText: 'More on Fundamentals', onClick }}
      firstLine={renderCalculatorButton()}
      isError={isError}
      fixedHeight
      onRetryAfterErrorClick={onErrorRetry}
      ref={ref}
      {...sortableWidgetProps}
    >
      <div className={cx('body')} data-testid="fundamentals-content">
        <div className={cx('containers')}>
          <Accordion exclusive={false}>{renderFundamentals()}</Accordion>
          <div className={cx('notes-container')}>{renderNotes()}</div>
        </div>
      </div>
    </Widget>
  );
};

export default forwardRef(FundamentalsWidget);
